// src/core/score/dealScore.ts
// PROPORA Deal-Score v2 -- reine Score-Funktion, keine React-/Fetch-Abhaengigkeit
// (gleiche Philosophie wie core/metrics/etw.ts). Ersetzt perspektivisch die fuenf
// unabhaengigen Score-Formeln je Analyzer und die duplizierten PRO-Breakdown-Kopien --
// vorerst nur fuer ETW verdrahtet, (der fruehere Feature-Flag ist entfernt).
//
// Spezifikation: zwei Saeulen a 50 Punkte, Objekt (Rendite ggue. Markt, Lage,
// NOI-Rendite) und Finanzierung (Cashflow, DSCR, Stresstest). Zwischen den
// Ankerpunkten linear interpoliert. Lage-Obergrenze deckelt den Gesamtscore bei
// schwacher Lage, unabhaengig davon, wie gut die uebrigen Kennzahlen sind.
//
// Kalibriert an 8 echten Objekten (siehe tests/deal-score.test.mjs) -- alle 8
// treffen den erwarteten Score exakt (0 Abweichung) und das erwartete Urteil.

export type LageInput =
  | { kind: "ok"; r: number; percentileK: number }
  | { kind: "ohne_plz" }
  | { kind: "plz_unbekannt" };

export type DealScoreRawInput = {
  /** Kaufpreis (bzw. Spielwiese-Ansicht, falls aktiv) -- fuer Bruttomietrendite. */
  kaufpreis: number;
  /** Kaufpreis + Nebenkosten -- fuer die NOI-Rendite. */
  gesamtinvestition: number;
  /** Rohe Jahreskaltmiete (Spielwiese-Ansicht, falls aktiv) -- Nenner fuer Bruttorendite und Cashflow-%. */
  jahreskaltmiete: number;
  /** NOI nach laufenden Kosten, vor Finanzierung. */
  noiJahr: number;
  /** Zins + Tilgung, 0 wenn financingOn=false. */
  annuitaetJahr: number;
  financingOn: boolean;
  /** NOI unter Stress (Miete -10%). */
  stressNoiJahr: number;
  /** Annuitaet unter Stress (Zins +1pp), 0 wenn financingOn=false. */
  stressAnnuitaetJahr: number;
  lage: LageInput;
  /** Zuschlag in Prozentpunkten auf r (PLZ-Wert UND Ersatzwert 5,5%). ETW: 0 (Default). */
  renditeZuschlagPp?: number;
  /**
   * Vermietungsstatus. "vollstaendig" (Default) = kein Deckel. Nur noch "nein" deckelt den
   * Score (Dominiks OK vom 12.10.2026, Nachtrag zu Punkt 2): "teilweise" bedeutet jetzt, dass
   * die tatsaechliche (Ist-)Miete bereits in jahreskaltmiete/noiJahr steckt (ueber die
   * bestehenden Leerstand-/Vacancy-Felder je Typ) -- der Score faellt dadurch organisch
   * niedriger aus, ohne zusaetzlichen Deckel. "nein" bedeutet: die Miete ist rein kalkuliert
   * (noch kein Mieter), deshalb bleibt dafuer die Obergrenze (Deckel 69) bestehen.
   */
  vermietungsstatus?: "vollstaendig" | "teilweise" | "nein";
};

export type DealScoreComponent = {
  key: string;
  label: string;
  /** Fachbegriff als kleiner Zusatz zum Einsteiger-Label, z. B. "DSCR" zu "Schuldendeckung". */
  technicalTerm?: string;
  points: number;
  maxPoints: number;
  rawValue: number | null;
};

export type DealScorePillar = {
  key: "objekt" | "finanzierung";
  label: string;
  points: number;
  maxPoints: number;
  components: DealScoreComponent[];
};

export type DealUrteil = "Guter Deal" | "Solide, mit Schwächen" | "Kritisch" | "Nicht empfehlenswert";

export type DealScoreResult = {
  /** 0..100, nach Lage-Obergrenze (falls angewendet). */
  score: number;
  /** 0..100, vor der Obergrenze -- fuer die Erklaerung ("waere ohne Obergrenze X"). */
  scoreRaw: number;
  urteil: DealUrteil;
  pillars: DealScorePillar[];
  obergrenzeApplied: boolean;
  /** true, wenn die Leerstand-Obergrenze (Deckel 69) gegriffen hat (nur bei vermietet="nein"). */
  leerstandObergrenzeApplied: boolean;
  hints: string[];
};

/** Ankerpunkt-Tripel/-Paar: [x, Punkte]. Mindestens 2, hoechstens 3 Punkte, steigend in x. */
type Anchor = readonly [number, number];

export type DealScoreConfig = {
  version: number;
  rendite: { anchors: readonly [Anchor, Anchor, Anchor]; weight: number };
  lage: {
    anchors: readonly [Anchor, Anchor];
    weight: number;
    obergrenzePctThreshold: number;
    obergrenzeCap: number;
    obergrenzeHinweis: string;
    ohnePlzAnnahmeR: number;
    ohnePlzPunkteAnteil: number;
    ohnePlzHinweis: string;
    plzUnbekanntHinweis: string;
  };
  noi: { anchors: readonly [Anchor, Anchor]; weight: number };
  cashflow: { anchors: readonly [Anchor, Anchor, Anchor]; weight: number };
  dscr: { anchors: readonly [Anchor, Anchor, Anchor]; weight: number };
  stress: { anchors: readonly [Anchor, Anchor]; weight: number };
  urteil: { gut: number; solide: number; kritisch: number };
  /** Zusaetzlicher, nicht gepunkteter Hinweis: Zins +2pp (ETW-Bruecke haengt den Hinweistext an). */
};

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/**
 * Mandatory-Hinweistext bei Leerstand-Obergrenze (vermietet="nein"), fuer alle Objektarten
 * identisch -- wortgleich mit dem Hinweis direkt am Vermietet-Umschalter in /check und allen
 * 5 Analyzern (dieselbe Konstante, re-exportiert aus dealScoreText.ts). Nachtest-Korrektur
 * 12.10.2026, Punkt 3: "zum Teil" entfernt -- bei "nein" ist die Miete GANZ kalkuliert
 * (kein Mieter da), nicht nur zum Teil; "zum Teil" galt fuer "teilweise" vermietet.
 */
export const LEERSTAND_OBERGRENZE_HINWEIS =
  "Die Miete ist nur kalkuliert. Bis ein Mieter gefunden ist, trägst du die Kosten allein. " +
  "Deshalb bewerten wir den Deal höchstens als solide.";

/** Stueckweise linear durch 2 oder 3 Ankerpunkte, ausserhalb des Bereichs auf den Randwert geklemmt. */
function interp(x: number, points: readonly Anchor[]): number {
  if (x <= points[0][0]) return points[0][1];
  if (x >= points[points.length - 1][0]) return points[points.length - 1][1];
  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    if (x >= x0 && x <= x1) return y0 + (y1 - y0) * (x - x0) / (x1 - x0);
  }
  return points[points.length - 1][1];
}

export function computeDealScore(input: DealScoreRawInput, config: DealScoreConfig): DealScoreResult {
  const hints: string[] = [];

  // --- Saeule Objekt (Rendite ggue. Markt, Lage, NOI-Rendite) ---
  const bruttoDeal = input.kaufpreis > 0 ? input.jahreskaltmiete / input.kaufpreis : 0;

  let renditeRaw: number;
  let renditeScore: number;
  let lageRaw: number | null = null;
  let lageScore: number;
  let obergrenzeApplied = false;
  const zuschlag = (input.renditeZuschlagPp ?? 0) / 100;

  if (input.lage.kind === "ok") {
    renditeRaw = (bruttoDeal - (input.lage.r + zuschlag)) * 100;
    renditeScore = interp(renditeRaw, config.rendite.anchors);
    lageRaw = input.lage.percentileK;
    lageScore = interp(input.lage.percentileK, config.lage.anchors);
    if (input.lage.percentileK < config.lage.obergrenzePctThreshold) obergrenzeApplied = true;
  } else {
    renditeRaw = (bruttoDeal - (config.lage.ohnePlzAnnahmeR + zuschlag)) * 100;
    renditeScore = interp(renditeRaw, config.rendite.anchors);
    lageScore = config.lage.ohnePlzPunkteAnteil;
    hints.push(input.lage.kind === "ohne_plz" ? config.lage.ohnePlzHinweis : config.lage.plzUnbekanntHinweis);
  }

  const noiYieldPct = input.gesamtinvestition > 0 ? (input.noiJahr / input.gesamtinvestition) * 100 : 0;
  const noiScore = interp(noiYieldPct, config.noi.anchors);

  const objektPoints = renditeScore + lageScore + noiScore;

  // --- Saeule Finanzierung (Cashflow, DSCR, Stresstest) ---
  const cfJahr = input.noiJahr - input.annuitaetJahr;
  const cfPct = input.jahreskaltmiete > 0 ? (cfJahr / input.jahreskaltmiete) * 100 : 0;
  const cashflowScore = interp(cfPct, config.cashflow.anchors);

  const dscr = input.annuitaetJahr > 0 ? input.noiJahr / input.annuitaetJahr : null;
  const dscrScore = !input.financingOn || dscr === null
    ? config.dscr.weight // Barkauf: voll
    : interp(dscr, config.dscr.anchors);

  const stressDscr = input.stressAnnuitaetJahr > 0 ? input.stressNoiJahr / input.stressAnnuitaetJahr : null;
  const stressScore = !input.financingOn || stressDscr === null
    ? config.stress.weight // Barkauf: kein Zinsrisiko, voll
    : interp(stressDscr, config.stress.anchors);

  const finanzierungPoints = cashflowScore + dscrScore + stressScore;

  const scoreRawTotal = objektPoints + finanzierungPoints;
  let scoreFinal = scoreRawTotal;
  if (obergrenzeApplied) {
    scoreFinal = Math.min(scoreFinal, config.lage.obergrenzeCap);
    hints.push(config.lage.obergrenzeHinweis);
  }
  // Nur "nein" deckelt (kalkulierte Miete, noch kein Mieter). "teilweise" wirkt bereits ueber
  // die reduzierte Ist-Miete im NOI/der Jahreskaltmiete, ohne zusaetzlichen Deckel (s. o.).
  const leerstandObergrenzeApplied = input.vermietungsstatus === "nein";
  if (leerstandObergrenzeApplied) {
    scoreFinal = Math.min(scoreFinal, config.lage.obergrenzeCap);
    hints.push(LEERSTAND_OBERGRENZE_HINWEIS);
  }
  const score = Math.round(clamp(scoreFinal, 0, 100));

  const urteil: DealUrteil =
    score >= config.urteil.gut ? "Guter Deal" :
    score >= config.urteil.solide ? "Solide, mit Schwächen" :
    score >= config.urteil.kritisch ? "Kritisch" :
    "Nicht empfehlenswert";

  const round1 = (x: number) => Math.round(x * 10) / 10;

  return {
    score,
    scoreRaw: round1(scoreRawTotal),
    urteil,
    obergrenzeApplied,
    leerstandObergrenzeApplied,
    hints,
    pillars: [
      {
        key: "objekt", label: "Objekt", points: round1(objektPoints), maxPoints: 50,
        components: [
          { key: "renditeMarkt", label: "Rendite gegenüber Markt", points: round1(renditeScore), maxPoints: config.rendite.weight, rawValue: renditeRaw },
          { key: "lage", label: "Lagequalität", points: round1(lageScore), maxPoints: config.lage.weight, rawValue: lageRaw },
          { key: "noi", label: "Nettorendite", technicalTerm: "NOI-Rendite auf Gesamtinvestition", points: round1(noiScore), maxPoints: config.noi.weight, rawValue: noiYieldPct },
        ],
      },
      {
        key: "finanzierung", label: "Finanzierung & Tragfähigkeit", points: round1(finanzierungPoints), maxPoints: 50,
        components: [
          { key: "cashflow", label: "Cashflow nach Zins & Tilgung", points: round1(cashflowScore), maxPoints: config.cashflow.weight, rawValue: cfPct },
          { key: "dscr", label: "Schuldendeckung", technicalTerm: "DSCR", points: round1(dscrScore), maxPoints: config.dscr.weight, rawValue: dscr },
          { key: "stress", label: "Krisenfestigkeit: Zins +1 Prozentpunkt, Miete −10 %", technicalTerm: "Stresstest", points: round1(stressScore), maxPoints: config.stress.weight, rawValue: stressDscr },
        ],
      },
    ],
  };
}
